/* ============================================================================
   imageToAscii.ts — Pipeline image → grille ASCII string[][].

   Étapes :
     1. Decode source (URL, dataURL, ImageData) → ImageData.
     2. Resample sur grille (cols × rows) où rows ≈ cols * aspect * 0.55
        (ratio largeur/hauteur des glyphs mono ≈ 0.55).
     3. Calcul luminance + applique brightness, contrast, optionnellement
        invert et dither Atkinson (pondéré par ditherAmount).
     4. Map luminance → glyph via le set choisi.

   Les params utilisés sont un sous-ensemble de CanvasParams (pour
   testabilité, on ne dépend pas du store complet).
   ============================================================================ */

import { getGlyphSet, luminanceToGlyph } from './glyphSets';
import type { CanvasParams } from '../canvasStore';

export type AsciiImageParams = Pick<
  CanvasParams,
  'glyphSet' | 'density' | 'contrast' | 'brightness' | 'invert' | 'ditherAmount'
> & {
  /**
   * Si défini, les pixels de luminance >= ce seuil deviennent ' ' (espace) au
   * lieu d'un glyph. Permet de "drop" un fond blanc/clair pour un rendu où
   * seul le sujet apparaît, le fond restant transparent dans le `<pre>`.
   * Default : 1.0 (jamais drop). Mode marthe : ~0.92.
   */
  whitespaceCutoff?: number;
};

/* ----------------------------------------------------------------------------
   Decode helpers
   ---------------------------------------------------------------------------- */

/**
 * Décode une URL/dataURL en ImageData via createImageBitmap + canvas 2D.
 * Throws si echec (image cassée).
 */
export async function decodeImage(src: string): Promise<ImageData> {
  // 1) fetch as blob (data URL ou http)
  const res = await fetch(src);
  const blob = await res.blob();
  // 2) decode
  const bitmap = await createImageBitmap(blob);
  // 3) draw on canvas 2D pour récupérer l'ImageData
  const canvas = document.createElement('canvas');
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D unavailable');
  ctx.drawImage(bitmap, 0, 0);
  bitmap.close?.();
  return ctx.getImageData(0, 0, canvas.width, canvas.height);
}

/* ----------------------------------------------------------------------------
   Resample → grille luminance [rows][cols] in [0..1]
   ---------------------------------------------------------------------------- */

/**
 * Downsample une ImageData en une grille de luminance [rows][cols].
 * Utilise un box average (rapide, suffisant pour ASCII).
 */
export function resampleLuminance(
  img: ImageData,
  cols: number,
  rows: number,
): number[][] {
  const { data, width: w, height: h } = img;
  const result: number[][] = [];
  const cellW = w / cols;
  const cellH = h / rows;

  for (let r = 0; r < rows; r++) {
    const row: number[] = new Array(cols);
    const y0 = Math.floor(r * cellH);
    const y1 = Math.min(h, Math.max(y0 + 1, Math.floor((r + 1) * cellH)));
    for (let c = 0; c < cols; c++) {
      const x0 = Math.floor(c * cellW);
      const x1 = Math.min(w, Math.max(x0 + 1, Math.floor((c + 1) * cellW)));
      let sum = 0;
      let count = 0;
      for (let y = y0; y < y1; y++) {
        for (let x = x0; x < x1; x++) {
          const idx = (y * w + x) * 4;
          // Rec. 601 luma
          const R = data[idx] ?? 0;
          const G = data[idx + 1] ?? 0;
          const B = data[idx + 2] ?? 0;
          sum += 0.299 * R + 0.587 * G + 0.114 * B;
          count++;
        }
      }
      row[c] = count > 0 ? sum / (count * 255) : 0;
    }
    result.push(row);
  }
  return result;
}

/* ----------------------------------------------------------------------------
   Adjustments + Atkinson dither
   ---------------------------------------------------------------------------- */

/** Apply brightness + contrast pivoté en 0.5. In-place. */
export function applyBrightnessContrast(
  grid: number[][],
  brightness: number,
  contrast: number,
): void {
  for (let r = 0; r < grid.length; r++) {
    const row = grid[r]!;
    for (let c = 0; c < row.length; c++) {
      let v = row[c]! * brightness;
      v = (v - 0.5) * contrast + 0.5;
      if (v < 0) v = 0;
      else if (v > 1) v = 1;
      row[c] = v;
    }
  }
}

/**
 * Atkinson dither (poids 1/8, 6 voisins). Pondéré par `amount` (0..1) :
 * blend entre erreur 0 et erreur full. Le levels parameter contrôle
 * la quantification (≥ 2). In-place.
 */
export function atkinsonDither(grid: number[][], levels: number, amount: number): void {
  const rows = grid.length;
  if (rows === 0) return;
  const cols = grid[0]!.length;
  const step = 1 / (levels - 1);
  const w = 1 / 8;

  for (let y = 0; y < rows; y++) {
    for (let x = 0; x < cols; x++) {
      const old = grid[y]![x]!;
      const quant = Math.round(old / step) * step;
      grid[y]![x] = quant;
      const errFull = old - quant;
      const err = errFull * amount; // amount=0 → no diffusion, =1 → full Atkinson
      if (err === 0) continue;
      const diffuse = (yy: number, xx: number) => {
        if (yy < 0 || yy >= rows || xx < 0 || xx >= cols) return;
        grid[yy]![xx] = (grid[yy]![xx]! + err * w);
      };
      diffuse(y, x + 1);
      diffuse(y, x + 2);
      diffuse(y + 1, x - 1);
      diffuse(y + 1, x);
      diffuse(y + 1, x + 1);
      diffuse(y + 2, x);
    }
  }
}

/* ----------------------------------------------------------------------------
   Map luminance grid → glyph grid
   ---------------------------------------------------------------------------- */

export function mapLuminanceToGlyphs(
  grid: number[][],
  glyphSet: string,
  whitespaceCutoff: number = 1.0,
): string[][] {
  const out: string[][] = [];
  for (let r = 0; r < grid.length; r++) {
    const row = grid[r]!;
    const rowOut: string[] = new Array(row.length);
    for (let c = 0; c < row.length; c++) {
      const lum = row[c]!;
      // Si pixel quasi-blanc (au-dessus du cutoff), output un espace
      // → invisible sur fond blanc, vide dans la grille = "fond transparent"
      if (lum >= whitespaceCutoff) {
        rowOut[c] = ' ';
      } else {
        rowOut[c] = luminanceToGlyph(lum, glyphSet);
      }
    }
    out.push(rowOut);
  }
  return out;
}

/* ----------------------------------------------------------------------------
   Pipeline complète : ImageData + params → string[][]
   ---------------------------------------------------------------------------- */

/**
 * Calcule (cols, rows) à partir d'une ImageData et de density.
 * cols ≈ density (clampé), rows ≈ cols * aspect * 0.55 (char ratio).
 */
export function computeGridDims(
  img: { width: number; height: number },
  density: number,
): { cols: number; rows: number } {
  const cols = Math.max(8, Math.min(400, Math.round(density)));
  const aspect = img.height / Math.max(1, img.width);
  const rows = Math.max(4, Math.round(cols * aspect * 0.55));
  return { cols, rows };
}

/**
 * Image → ASCII grid string[][]. Pas async (decodage à part via decodeImage).
 */
export function imageToAsciiGrid(
  img: ImageData,
  params: AsciiImageParams,
): string[][] {
  const { cols, rows } = computeGridDims(img, params.density);
  const lum = resampleLuminance(img, cols, rows);
  applyBrightnessContrast(lum, params.brightness, params.contrast);
  const set = getGlyphSet(params.glyphSet, params.invert);
  if (params.ditherAmount > 0) {
    atkinsonDither(lum, set.length, params.ditherAmount);
  }
  return mapLuminanceToGlyphs(lum, set, params.whitespaceCutoff ?? 1.0);
}
